import { track } from '@/features/shared/analytics/trackingQueue'
import {
  AnalyticsEvent,
  type FilterUtilityViewed,
  type FilterUtilityClicked,
} from '@shipt/analytics-member-web'
import { urlPathnameToLocationName } from '@/features/shared/analytics/utils'
import { type SearchParams } from '@/features/shop/services/Search/types'
import { getObjectKeys } from '@/features/shared/utils/types'
import { validSearchFilterParams } from '@/features/shop/services/Search/constants'
import { useSearchQueryParams } from '@/features/shared/hooks/search'

type UtilityType = 'filter' | 'dropdown' | 'popular_cards'

type FilterUtilityViewedEvent = Omit<
  FilterUtilityViewed['properties'],
  'location' | 'utility_type'
> & { utility_type: UtilityType }

type FilterUtilityClickedEvent = Omit<
  FilterUtilityClicked['properties'],
  'location' | 'utility_type' | 'applied_filter_values'
> & { utility_type: UtilityType; params?: SearchParams; lineage?: string }

export const trackFilterUtilityViewed = (eventProperties: FilterUtilityViewedEvent) =>
  track({
    eventName: AnalyticsEvent.FilterUtilityViewed,
    properties: {
      ...eventProperties,
      location: urlPathnameToLocationName(),
    },
  })

const isValidSearchParamKey = (key: string): key is keyof SearchParams =>
  key in validSearchFilterParams

export const useTrackFilterUtilityClicked = () => {
  const searchParams = useSearchQueryParams()

  return ({ params, lineage, ...eventProperties }: FilterUtilityClickedEvent) => {
    const filterParams = {
      ...searchParams,
      // there are no new params applied when the utility_type is 'dropdown'
      // since clicking on the filter just toggles the display of filters
      ...(eventProperties.utility_type === 'filter' && params),
    }
    const categories = lineage ? lineage.split(' > ') : []
    const appliedFilterValues = getObjectKeys(filterParams).reduce<string[]>((acc, key) => {
      const value = filterParams[key]
      return isValidSearchParamKey(key)
        ? [...acc, ...(Array.isArray(value) ? value : [String(value)])]
        : acc
    }, categories)
    track({
      eventName: AnalyticsEvent.FilterUtilityClicked,
      properties: {
        ...eventProperties,
        location: urlPathnameToLocationName(),
        applied_filter_values: appliedFilterValues,
      },
    })
  }
}
